import { uniqueId } from 'lodash'
import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'
import { CaptionLayer, CaptionLayerProps, ImageLayer, ImageLayerProps, Layer } from '../types'
import UgeLayerAction from '../types/layer-action'

type ContextProps = {
    activeLayerId?: string
    layers: Layer[]
    captions: CaptionLayer[]
    images: ImageLayer[]
    addLayer: (layer: Layer, successMessage?: string) => void
    performLayerAction: (layer: Layer, action: UgeLayerAction) => void
    removeLayer: (id: string, successMessage?: string) => void
    updateLayer: (id: string, props: Partial<ImageLayerProps | CaptionLayerProps>) => void
    updateLayerIndex: (id: string, index: number) => void
    setActiveLayerId: (id?: string) => void
    setLayers: (layers: Layer[]) => void
}

type Props = {
    children?: ReactNode
    defaultLayers?: Layer[]
}

export const UgeLayersContext = createContext({} as ContextProps)

const UgeLayersContextManager = ({ children, defaultLayers = [] }: Props) => {
    const [layers, setLayers] = useState<Layer[]>(defaultLayers)
    const [activeLayerId, setActiveLayerId] = useState<string | undefined>()

    const { captions, images } = useMemo(() => {
        const captions = layers.filter((layer) => layer.type === 'caption') as CaptionLayer[]
        const images = layers.filter((layer) => layer.type === 'image') as ImageLayer[]

        return {
            captions,
            images,
        }
    }, [layers])

    const addLayer = useCallback(
        (layer: Layer) => {
            setLayers((state) => [...state, layer])
        },
        [layers]
    )

    const removeLayer = useCallback(
        (id: string) => {
            setLayers((state) => state.filter((layer) => layer.id !== id))
        },
        [layers]
    )

    const updateLayer = useCallback(
        (id: string, props: Partial<ImageLayerProps | CaptionLayerProps>) => {
            setLayers((state) =>
                state.map((layer) => {
                    if (layer.id === id) {
                        return { ...layer, props: { ...layer.props, ...props } }
                    }

                    return layer
                })
            )
        },
        [layers]
    )

    const updateLayerIndex = useCallback(
        (id: string, index: number) => {
            setLayers((state) => {
                const layerIndex = state.findIndex((layer) => layer.id === id)

                if (layerIndex > -1 && layerIndex !== index) {
                    const clone = [...state]
                    const layer = clone.splice(layerIndex, 1)

                    if (layer.length) {
                        clone.splice(index, 0, layer[0])
                    }

                    return clone
                }

                return state
            })
        },
        [layers]
    )

    const performLayerAction = useCallback(
        (layer: Layer, action: UgeLayerAction) => {
            switch (action) {
                case 'delete':
                    removeLayer(layer.id)
                    break
                case 'duplicate':
                    addLayer({
                        ...layer,
                        id: uniqueId('uge-duplicate-'),
                    })
                    break
                case 'flip':
                    updateLayer(layer.id, {
                        mirror: !layer.props.mirror,
                    })
                    break
                case 'send-to-back':
                    updateLayerIndex(layer.id, 0)
                    break
            }
        },
        [layers]
    )

    return (
        <UgeLayersContext.Provider
            value={{
                activeLayerId,
                layers,
                captions,
                images,
                addLayer,
                performLayerAction,
                removeLayer,
                updateLayer,
                updateLayerIndex,
                setActiveLayerId,
                setLayers,
            }}
        >
            {children}
        </UgeLayersContext.Provider>
    )
}

export default UgeLayersContextManager
